import { Card, Stack, Table, Button, TableBody, Typography, TableContainer } from '@mui/material';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import TableHeadCustom from 'src/components/table/table-head-custom';
import TableNoData from 'src/components/table/table-no-data';
import { BankAccountType } from 'src/type/bank';
import { useBoolean } from 'src/hooks/use-boolean';
import { useState, useEffect, useCallback } from 'react';
import useBankOptions from 'src/hooks/use-bank-option';
import { toastError } from 'src/utils/format-error';
import axios, { endpoints } from 'src/utils/axios';
import TableLoading from 'src/components/table/table-loading';
import { Pagination } from 'src/type/utils';
import SettingBankFormDialog from './setting-bank-form-dialog';
import SettingBankAccountRow from './setting-bank-account-row';

const tableHead = [
  { id: 'code', label: 'ธนาคาร' },
  { id: 'createdAt', label: 'เลขบัญชี', width: 180 },
  { id: 'usedAt', label: 'ชื่อบัญชี', width: 180 },
  { id: 'createdBy', label: 'สถานะ', width: 180 },
  { id: 'action', width: 60 },
];

export default function SettingBankAccount() {
  const form = useBoolean();
  const loading = useBoolean();
  const initialize = useBoolean(true);
  const [bankAccount, setBankAccount] = useState(Array<BankAccountType>());
  const reload = useBoolean();
  const [focusBankAccount, setFocusBankAccount] = useState<BankAccountType | null>(null);
  const { bankOptions } = useBankOptions();

  useEffect(() => {
    load();
  }, [reload.value]);

  const load = async () => {
    try {
      const { data } = await axios.get<Pagination<BankAccountType>>(endpoints.backAccount.get);
      setBankAccount(data.results);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  };

  const forceReload = useCallback(() => {
    reload.onToggle();
  }, []);

  const handleOpenCreateForm = useCallback(() => {
    setFocusBankAccount(null);
    form.onTrue();
  }, []);

  const handleOpenEditForm = useCallback((account: BankAccountType) => {
    setFocusBankAccount(account);
    form.onTrue();
  }, []);

  const handleCloseForm = useCallback(() => {
    setFocusBankAccount(null);
    form.onFalse();
  }, []);

  const handleSetDefaultBank = useCallback(async (account: BankAccountType) => {
    try {
      loading.onTrue();
      await axios.patch(endpoints.backAccount.setDefault(account.id));
      forceReload();
    } catch (error) {
      toastError(error);
    } finally {
      loading.onFalse();
    }
  }, []);

  const handleToggleStatus = useCallback(async (account: BankAccountType) => {
    try {
      loading.onTrue();
      await axios.patch(endpoints.backAccount.update(account.id), {
        isEnable: !account.isEnable,
      });
      forceReload();
    } catch (error) {
      toastError(error);
    } finally {
      loading.onFalse();
    }
  }, []);

  const handleDeleteBank = useCallback(async (account: BankAccountType) => {
    try {
      loading.onTrue();
      await axios.delete(endpoints.backAccount.delete(account.id));
      forceReload();
    } catch (error) {
      toastError(error);
    } finally {
      loading.onFalse();
    }
  }, []);

  return (
    <>
      <Card sx={{ py: 5, px: 3 }}>
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h4" gutterBottom>
              บัญชีธนาคาร
            </Typography>
            <Button
              variant="contained"
              startIcon={<Iconify icon="mingcute:add-line" />}
              size="medium"
              onClick={handleOpenCreateForm}
            >
              เพิ่มบัญชีธนาคาร
            </Button>
          </Stack>

          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              <Table sx={{ minWidth: 800 }}>
                <TableHeadCustom headLabel={tableHead} />

                <TableBody>
                  <TableLoading loading={initialize.value} rows={5}>
                    {bankAccount.map((row) => (
                      <SettingBankAccountRow
                        key={row.id}
                        row={row}
                        loading={loading.value}
                        onEdit={() => handleOpenEditForm(row)}
                        onSetDefault={() => handleSetDefaultBank(row)}
                        onToggleStatus={() => handleToggleStatus(row)}
                        handleDelete={() => handleDeleteBank(row)}
                      />
                    ))}

                    <TableNoData notFound={!bankAccount.length} />
                  </TableLoading>
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>
        </Stack>
      </Card>
      <SettingBankFormDialog
        open={form.value}
        onClose={handleCloseForm}
        forceReload={forceReload}
        bankAccount={focusBankAccount}
        bankOptions={bankOptions}
      />
    </>
  );
}
